import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { mergeMap, Observable, of } from 'rxjs';

import { EcmDispatcherModule } from '@ecm/dispatcher';
import { Contract, PortfolioService, Role, UrlHelper } from '@ecm/portfolio';

import { ErrorPageService } from '../services/error-page.service';
import { RouterService } from '../services/router.service';

@Injectable({
    providedIn: 'root',
})
export class PortfolioResolver {
    /**
     * Class name for logger
     * @type {string}
     */
    private static CLASS_NAME = 'PortfolioResolver';

    public constructor(
        private portfolioService: PortfolioService,
        private routerService: RouterService,
        private errorPageService: ErrorPageService,
        private cookieService: CookieService,
    ) {}

    /**
     * Resolves route only if CID is present in route and CID belongs to user portfolio
     * This should be used as a guard and not a resolver, but Angular router doesn't allow to chain guards
     * (this guard should be executed after AuthGuard)
     * @param {ActivatedRouteSnapshot} route
     * @param {RouterStateSnapshot} state
     * @returns {Observable<boolean>}
     * @deprecated Use UsagePortfolioResolver or ContractPortfolioResolver instead
     */
    public resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        // get current url
        const url: string = state.url;
        // perform cid regex on url
        const cidMatch: string[] = UrlHelper.matchCidInPath(url);
        if (cidMatch) {
            // use second match (first match can't be used because it captures slashes arround cid)
            const cid: string = cidMatch[1];
            return this.portfolioService.getContractByCid(cid).pipe(
                mergeMap((contract: Contract) => {
                    if (contract) {
                        if (
                            contract.telco &&
                            contract.telco.state &&
                            contract.telco.state.toUpperCase() === 'VIM_ELIGIBLE'
                        ) {
                            // don't allow VIM_ELIGIBLE
                            EcmDispatcherModule.sendConsoleError(
                                PortfolioResolver.CLASS_NAME,
                                cid + ' is a VIM_ELIGIBLE contract, will redirect to HP',
                            );
                            this.routerService.navigateToHome();
                            return of(false);
                        } else if (
                            contract.role &&
                            contract.role.toUpperCase() === Role.UTILISATEUR &&
                            contract.telco &&
                            contract.telco.marketType &&
                            contract.telco.state &&
                            contract.telco.marketType.toUpperCase() === 'RES' &&
                            contract.telco.state.toUpperCase() === 'LOGGED'
                        ) {
                            // don't allow FICTIF (BAL secondaire)
                            EcmDispatcherModule.sendConsoleError(
                                PortfolioResolver.CLASS_NAME,
                                cid + ' is a FICTIF (BAL secondaire) contract, will redirect to HP',
                            );
                            this.routerService.navigateToHome();
                            return of(false);
                        } else {
                            EcmDispatcherModule.sendConsoleInfo(
                                PortfolioResolver.CLASS_NAME,
                                cid + ' matches portfolio, will let go to route ' + url,
                            );
                            // set coookie
                            this.setContractCookie(contract);
                            // set currentContract
                            this.routerService.storeAndDispatchCurrentContract(contract);
                            return of(true);
                        }
                    } else {
                        EcmDispatcherModule.sendConsoleError(
                            PortfolioResolver.CLASS_NAME,
                            cid + " doesn't match portfolio, will redirect to HP",
                        );
                        this.routerService.navigateToHome();
                        return of(false);
                    }
                }),
            );
        } else {
            EcmDispatcherModule.sendConsoleError(
                PortfolioResolver.CLASS_NAME,
                'No CID provided in route ' + url + ', will display default error page',
            );
            this.errorPageService.showDefaultErrorPage();
            return of(false);
        }
    }

    /**
     * Set cookie for contract - 2h duration - if the info is given by PortfolioService.
     * @param {Contract} contract
     */
    private setContractCookie(contract: Contract): void {
        if (contract.telco.cookieContract) {
            const expireDate = new Date();
            expireDate.setHours(expireDate.getHours() + 2);
            this.cookieService.set('contrat', contract.telco.cookieContract, expireDate, '/', '.orange.fr', true);
            EcmDispatcherModule.sendConsoleInfo(
                PortfolioResolver.CLASS_NAME,
                '[Contract Cookie] ' + contract.telco.cookieContract + ' has been written',
            );
        }
    }
}
